// src/pages/subscription/DiscountListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionDiscount, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { DiscountFormDialog } from '@/features/subscription/DiscountFormDialog';
import { formatDate, formatMoney, humanizeEnum } from '@/features/subscription/format';

function formatDiscountValue(type: string, value: number | null | undefined, currency?: string | null): string {
  if (value == null) return '--';
  switch (type) {
    case 'DISCOUNT_TYPE_PERCENTAGE': return `${value}%`;
    case 'DISCOUNT_TYPE_FIXED_AMOUNT': return formatMoney(value, currency);
    case 'DISCOUNT_TYPE_FREE_PERIOD': return `${value} months free`;
    default: return String(value);
  }
}

export const DiscountListPage = observer(() => {
  const store = useSubscriptionDiscount();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    store.ListDiscounts({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], view: 0,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm(t('subscription.deleteDiscountConfirm'))) return;
    try {
      await store.DeleteDiscount(itemId);
      store.ListDiscounts({
        pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
        sort: [], view: 0,
      });
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="sr-only">Discounts</h1>
          <nav className="text-sm">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Discounts</span>
          </nav>
        </div>
        <Button onClick={() => setShowCreate(true)}>{t('subscription.discount.create')}</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Redemptions</TableHead>
              <TableHead>Valid Period</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={8} message="No discounts found" />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link to={`/subscriptions/discounts/${itemId}`} className="text-primary hover:underline font-mono">
                        {String(item.code ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>{String(item.name ?? '--')}</TableCell>
                    <TableCell>
                      {item.type ? (
                        <Badge variant="primary" size="sm">{humanizeEnum(item.type, 'DISCOUNT_TYPE_')}</Badge>
                      ) : '--'}
                    </TableCell>
                    <TableCell>{formatDiscountValue(item.type, item.value, item.currency)}</TableCell>
                    <TableCell>
                      {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
                    </TableCell>
                    <TableCell>
                      {String(item.redemption_count ?? item.current_redemptions ?? 0)}
                      {item.max_redemptions ? ` / ${item.max_redemptions}` : ''}
                    </TableCell>
                    <TableCell>
                      {formatDate(item.starts_at)} - {formatDate(item.ends_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/discounts/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(itemId)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <DiscountFormDialog
        open={showCreate}
        discount={null}
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          store.ListDiscounts({
            pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
            sort: [], view: 0,
          });
        }}
      />
    </div>
  );
});

export default DiscountListPage;
